import Phaser from 'phaser';
import {
  appearanceCatalog, findFurniture, isPcSheetRef, isScooterDirection, scooterFurnitureId,
  type AvatarAppearance, type PlayerAppearance, type ScooterDirection, type ScooterModel,
} from '@campus/core';
import { ensureFurnitureTextures, hasFurnitureTexture, spawnFurnitureImage } from '../loaders/FurnitureTextures.js';
import { API_BASE } from '../../lib/apiBase.js';

// ── Types ───────────────────────────────────────────────────────────────────

interface AnimDef {
  start: number;
  end: number;
  fps: number;
}

interface CatalogEntry {
  id: string;
  url: string;
  layer: string;
  zIndex: number;
  recolorable: boolean;
  frameWidth: number;
  frameHeight: number;
  animations: Record<string, AnimDef>;
}

const catalog = appearanceCatalog as CatalogEntry[];

// ── Constants ───────────────────────────────────────────────────────────────

const FRAME_W = 16;
const FRAME_H = 32;

// Couleurs de statut du design system Placio (thème sombre : --color-status-*)
const STATUS_HEX: Record<string, number> = {
  available: 0xa8f0c6,
  away:      0xffd166,
  busy:      0xf07070,
  focus:     0x7F77DD,
};

// ── Étiquette de nom (style design system : pastille --color-hud, Inter) ──

const UI_DEPTH = 500;                 // toujours au-dessus des murs
const LABEL_FONT = '"Inter", system-ui, -apple-system, "Segoe UI", sans-serif';
const LABEL_BG = 0x0d0f14;            // --color-hud (thème sombre)
const LABEL_BG_ALPHA = 0.84;
const LABEL_PAD_X = 8;
const LABEL_H = 20;
const LABEL_RADIUS = 6;               // --radius-sm
const LABEL_DOT_R = 4;
const LABEL_DOT_GAP = 5;
const LABEL_STACK_GAP = 2;            // écart entre deux étiquettes empilées
const LABEL_MAX_CHARS = 12;

/** Prénom seul, tronqué à 12 caractères — le nom complet s'affiche au survol. */
export function shortDisplayName(name: string): string {
  const first = name.trim().split(/\s+/)[0] ?? '';
  return first.length > LABEL_MAX_CHARS ? `${first.slice(0, LABEL_MAX_CHARS - 1)}…` : first;
}

// Avatars vivants par scène : sert à empiler les étiquettes qui se chevauchent.
const avatarsByScene = new WeakMap<Phaser.Scene, Set<CompositeAvatar>>();
const lastLayoutFrame = new WeakMap<Phaser.Scene, number>();
/** Scènes où les avatars sont recalés juste avant le rendu (un seul écouteur par scène, gardé d'une carte à l'autre). */
const syncedScenes = new WeakSet<Phaser.Scene>();

/**
 * Empile les étiquettes qui se chevauchent : de l'avatar le plus au premier
 * plan (y le plus grand) au plus au fond, chaque étiquette remonte au-dessus
 * de celles déjà placées qu'elle toucherait. Une fois par frame et par scène.
 */
function layoutLabels(avatars: Set<CompositeAvatar>): void {
  const list = [...avatars].sort((a, b) => b.y - a.y);
  const placed: Array<{ l: number; r: number; t: number; b: number }> = [];
  for (const avatar of list) {
    const half = avatar.labelWidth / 2;
    const l = avatar.x - half;
    const r = avatar.x + half;
    let bottom = avatar.naturalLabelBottom;
    let moved = true;
    while (moved) {
      moved = false;
      for (const p of placed) {
        if (l < p.r && r > p.l && bottom - LABEL_H < p.b && bottom > p.t) {
          bottom = p.t - LABEL_STACK_GAP;
          moved = true;
        }
      }
    }
    placed.push({ l, r, t: bottom - LABEL_H, b: bottom });
    avatar.labelBottom = bottom;
  }
}

const DEFAULT_APPEARANCE: PlayerAppearance = {
  bodyId: 'body_01',
  eyesId: 'eyes_01',
  hairId: 'hairstyle_01_01',
  hairColor: 0xFFAAAA,
  outfitId: 'outfit_01_01',
  accessoryId: null,
};

// ── Ordered layer slots ─────────────────────────────────────────────────────

const LAYER_ORDER: readonly string[] = [
  'body',      // zIndex 0
  'eyes',      // zIndex 1
  'outfit',    // zIndex 2
  'hair',      // zIndex 4
  'accessory', // zIndex 5
];

// ── Helpers ─────────────────────────────────────────────────────────────────

function lookupEntry(id: string): CatalogEntry | undefined {
  return catalog.find((e) => e.id === id);
}

function animKey(textureKey: string, anim: string): string {
  return `cv_${textureKey}_${anim}`;
}

function ensureAnimations(scene: Phaser.Scene, textureKey: string): void {
  const anims = scene.anims;
  const entry = lookupEntry(textureKey);
  if (!entry) return;

  const texture = scene.textures.get(textureKey);
  const totalFrames = texture.frameTotal - 1; // frameTotal includes __BASE

  for (const [key, def] of Object.entries(entry.animations)) {
    const fullKey = animKey(textureKey, key);
    if (anims.exists(fullKey)) continue;

    if (def.start >= totalFrames || def.end >= totalFrames) {
      console.warn(`[CompositeAvatar] Frames hors limites: ${textureKey} ${key} start=${def.start} end=${def.end} total=${totalFrames}`);
      continue;
    }

    anims.create({
      key: fullKey,
      frames: anims.generateFrameNumbers(textureKey, {
        start: def.start,
        end: def.end,
      }),
      frameRate: def.fps,
      repeat: key === 'idle' ? 0 : -1,
    });
  }
}

// ── Avatars Pixel Citizen ───────────────────────────────────────────────────
// Planche assemblée par l'API (`@campus/core/pixel-citizen`), une seule texture
// par joueur. Cases 48×48 : une rangée par direction (bas, gauche, droite,
// haut), colonnes 0-3 repos, 4-9 marche, 10-15 course.

const PC_FRAME = 48;
/** Colonnes d'une planche : 16 (Pixel Citizen), 20 (Campus avec poses assises) — lues dans la texture. */
const sheetColumns = (scene: Phaser.Scene, key: string) => Math.round(scene.textures.get(key).source[0]!.width / PC_FRAME);
const PC_ROWS: Record<string, number> = { down: 0, left: 1, right: 2, up: 3 };
const PC_FEET = 3; // pixels transparents sous les pieds dans la case
/**
 * Taille réelle (×1) : personnage d'environ 38 px à l'écran, un peu plus petit
 * que les avatars LimeZu (44 px, jugés trop grands). Échelle entière : pixels nets.
 */
const PC_SCALE = 1;
/** Haut de la tête au-dessus des pieds, avant mise à l'échelle. */
const PC_HEAD_TOP = 44;
/**
 * Avatars Campus (v3) : leur marche (6 images à 10 images/s) est dessinée pour
 * le pas du prototype de Charles, une case de 32 px en 190 ms. Plus vite, les
 * pieds glissent sur le sol (surtout de profil) : la cadence suit donc la
 * vitesse réelle de l'avatar (marche, sprint, joueurs distants compris).
 */
const CAMPUS_STEP_SPEED = 32 / 0.19; // px/s
/** Frappe au clavier (images 17-18, touche C quand on est assis). */
const TYPING_FPS = 6;
/** Assis, la tête descend d'environ 8 px (fesses à 10 px du sol au lieu des pieds) : le nom suit. */
const SEATED_HEAD_DROP = 8;

const CAMPUS_SPEED_SMOOTHING = 0.2;

// ── Trottinette ─────────────────────────────────────────────────────────────
// L'avatar debout sur le plateau : image de repos de sa direction, remontée
// de la hauteur du plateau, la trottinette (meuble du catalogue, vue de la
// même direction) dessinée sous ses pieds. Petit rebond d'1 px en roulant.

/** Hauteur (px) dont l'avatar monte sur la trottinette, selon la vue (de dos, on voit le plateau plus haut). */
const RIDE_LIFT: Record<ScooterDirection, number> = { down: 6, up: 8, left: 7, right: 7 };
/** Bas de la trottinette sous les pieds (ombre comprise). */
const RIDE_GROUND = 2;
/** De profil, l'avatar se tient vers l'arrière du plateau : la colonne reste devant lui. */
const RIDE_SHIFT: Record<ScooterDirection, number> = { down: 0, up: 0, left: 3, right: -3 };
const RIDE_BOB_MS = 110;

const pcTextureKey = (sheet: string) => `pc_${sheet}`;
const pcSheetUrl = (sheet: string) => `${API_BASE}/assets/shared/avatars/pc/${sheet}.png`;

function ensurePixelCitizenAnims(scene: Phaser.Scene, key: string): void {
  const columns = sheetColumns(scene, key);
  for (const [dir, row] of Object.entries(PC_ROWS)) {
    // Repos figé sur la première image (pas de respiration), marche sur 6 images
    const defs: Array<[string, number, number, number]> = [
      [`idle_${dir}`, 0, 0, 1],
      [`walk_${dir}`, 4, 9, 10],
    ];
    for (const [anim, start, end, fps] of defs) {
      const fullKey = animKey(key, anim);
      if (scene.anims.exists(fullKey)) continue;
      scene.anims.create({
        key: fullKey,
        frames: scene.anims.generateFrameNumbers(key, { start: row * columns + start, end: row * columns + end }),
        frameRate: fps,
        repeat: -1,
      });
    }
  }
  // Poses assises (planches Campus de 20 colonnes, fiche #30 de Charles) :
  // 16 assis immobile, 17-18 frappe au clavier.
  if (columns >= 19) {
    for (const [dir, row] of Object.entries(PC_ROWS)) {
      const sit = animKey(key, `sit_${dir}`);
      if (!scene.anims.exists(sit)) {
        scene.anims.create({ key: sit, frames: scene.anims.generateFrameNumbers(key, { start: row * columns + 16, end: row * columns + 16 }), frameRate: 1, repeat: -1 });
      }
      const type = animKey(key, `type_${dir}`);
      if (!scene.anims.exists(type)) {
        scene.anims.create({ key: type, frames: scene.anims.generateFrameNumbers(key, { start: row * columns + 17, end: row * columns + 18 }), frameRate: TYPING_FPS, repeat: -1 });
      }
    }
  }
  const idle = animKey(key, 'idle');
  if (!scene.anims.exists(idle)) {
    scene.anims.create({ key: idle, frames: scene.anims.generateFrameNumbers(key, { start: 0, end: 0 }), frameRate: 1, repeat: -1 });
  }
}

// ── CompositeAvatar ─────────────────────────────────────────────────────────

/**
 * CompositeAvatar — Multi-layer avatar with physics anchor.
 *
 * Uses a Phaser.GameObjects.Zone as an invisible physics body (for Arcade
 * collisions and camera follow). Layer sprites are added directly to the
 * scene and follow the anchor position each frame via preUpdate on body sprite.
 *
 * Also manages UI overlays: name label, status dot, mute badge.
 */
export class CompositeAvatar extends Phaser.GameObjects.Sprite {
  private layerSprites = new Map<string, Phaser.GameObjects.Sprite>();
  private currentAnim: string | null = null;
  private _depth = 10;
  private _scale = 2;
  // Trottinette : modèle, sprite (meuble du catalogue), compteur de génération
  // contre un chargement de texture périmé.
  private vehicle: ScooterModel | null = null;
  private vehicleSprite: Phaser.GameObjects.Image | null = null;
  private vehicleSpriteId: string | null = null;
  private vehicleGeneration = 0;
  private moving = false;
  /** Assis (planches Campus) : direction du siège, frappe au clavier, relèvement de l'image (`seatSpots`). */
  private seat: { direction: string; typing: boolean; lift: number } | null = null;

  // UI overlays
  private readonly nameBg: Phaser.GameObjects.Graphics;
  private readonly nameLabel: Phaser.GameObjects.Text;
  private fullName: string;
  private drawnBgWidth = 0;
  /** Largeur de l'étiquette (fond compris) — lue par layoutLabels(). */
  labelWidth = 0;
  /** Bas de l'étiquette après empilement — écrit par layoutLabels(). */
  labelBottom = 0;
  private readonly statusDot: Phaser.GameObjects.Arc;
  private readonly muteBg: Phaser.GameObjects.Rectangle;
  private readonly muteLabel: Phaser.GameObjects.Text;
  private _isMuted = false;
  private _muteTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    displayName = '',
  ) {
    // Invisible anchor sprite — 1x1 transparent, serves as physics body
    super(scene, x, y, '__DEFAULT');
    this.setVisible(false);
    this.setScale(this._scale);
    scene.add.existing(this);

    // ── Name background (pastille arrondie, redessinée si la largeur change) ──
    this.nameBg = scene.add.graphics();

    // ── Name label — prénom seul, nom complet au survol ──
    this.fullName = displayName;
    this.nameLabel = scene.add.text(x, y, shortDisplayName(displayName), {
      fontFamily: LABEL_FONT,
      fontSize: '12px',
      fontStyle: 'bold',
      color: '#e8e9ee',
      resolution: 2,
    }).setOrigin(0, 0.5);
    this.nameLabel.setInteractive();
    this.nameLabel.on('pointerover', () => this.nameLabel.setText(this.fullName));
    this.nameLabel.on('pointerout', () => this.nameLabel.setText(shortDisplayName(this.fullName)));
    // Inter peut finir de charger après la création : re-rendre le texte.
    void document.fonts?.ready.then(() => {
      if (this.nameLabel.active) this.nameLabel.updateText();
    });

    // ── Status dot ─────────────────────────────────────
    this.statusDot = scene.add.circle(x, y, 4, STATUS_HEX['available'])
      .setOrigin(0, 0.5);

    // ── Mute badge (hidden by default) ─────────────────
    this.muteBg = scene.add.rectangle(x, y, 36, 14, 0x7f1d1d, 0.7)
      .setOrigin(0.5, 1).setVisible(false);
    this.muteLabel = scene.add.text(x, y, 'MUTE', {
      fontSize: '10px',
      color: '#ffffff',
      stroke: '#ffffff',
      strokeThickness: 1,
      resolution: 2,
    }).setOrigin(0.5, 1).setVisible(false);

    // Register for preUpdate ticks
    scene.sys.updateList.add(this);

    let avatars = avatarsByScene.get(scene);
    if (!avatars) {
      avatars = new Set();
      avatarsByScene.set(scene, avatars);
    }
    avatars.add(this);
    if (!syncedScenes.has(scene)) {
      syncedScenes.add(scene);
      scene.events.on(Phaser.Scenes.Events.PRE_RENDER, () => {
        const { time, delta } = scene.game.loop;
        for (const avatar of avatarsByScene.get(scene) ?? []) avatar.syncToAnchor(time, delta);
      });
    }
  }

  /** Bas de l'étiquette sans empilement : juste au-dessus de la tête. */
  get naturalLabelBottom(): number {
    const lift = this.vehicle ? RIDE_LIFT[this.facing] : 0;
    const drop = this.seat && this.campusSheet ? SEATED_HEAD_DROP - this.seat.lift : 0;
    if (this.pcSheet) return this.y - PC_HEAD_TOP * PC_SCALE - lift + drop;
    return this.y - FRAME_H * this._scale - 3 - lift;
  }

  // ── Assise ──────────────────────────────────────────────

  /** S'assoit (direction du siège, frappe au clavier) ou se lève (null). */
  setSeat(seat: { direction: string; typing: boolean; lift: number } | null): void {
    if (seat?.direction === this.seat?.direction && seat?.typing === this.seat?.typing && seat?.lift === this.seat?.lift) return;
    this.seat = seat;
    const dir = seat?.direction ?? this.facing;
    this.playCompositeAnimation(`idle_${dir}`);
  }

  get isSeated(): boolean {
    return this.seat !== null;
  }

  /** Assis de dos (devant un bureau) : le dossier du siège passe devant l'avatar. */
  get seatedFacingUp(): boolean {
    return this.seat?.direction === 'up';
  }

  // ── Trottinette ─────────────────────────────────────────

  /** Monte sur une trottinette de ce modèle, ou en descend (null). */
  setVehicle(model: ScooterModel | null): void {
    if (model === this.vehicle) return;
    this.vehicle = model;
    const generation = ++this.vehicleGeneration;
    this.destroyVehicleSprite();
    if (model) {
      const entries = (['up', 'down', 'left', 'right'] as const)
        .map((d) => findFurniture(scooterFurnitureId(model, d)))
        .filter((e) => e !== undefined);
      void ensureFurnitureTextures(this.scene, entries).then(() => {
        if (this.active && generation === this.vehicleGeneration) this.syncVehicleSprite();
      });
    }
    // Rejoue l'animation : sur la trottinette, l'avatar reste sur son image de repos.
    if (this.currentAnim) this.playCompositeAnimation(this.currentAnim);
  }

  /** Direction de l'avatar, lue dans son animation courante (idle_left, walk_up…). */
  private get facing(): ScooterDirection {
    const dir = this.currentAnim?.split('_')[1];
    return isScooterDirection(dir) ? dir : 'down';
  }

  /** Sprite de la trottinette tourné dans la direction de l'avatar (recréé quand elle change). */
  private syncVehicleSprite(): void {
    if (!this.vehicle) return;
    const id = scooterFurnitureId(this.vehicle, this.facing);
    if (this.vehicleSprite && this.vehicleSpriteId === id) return;
    const entry = findFurniture(id);
    if (!entry || !hasFurnitureTexture(this.scene, entry)) return;
    this.destroyVehicleSprite();
    this.vehicleSprite = spawnFurnitureImage(this.scene, entry, this.x - entry.dimensions.w / 2, this.y + RIDE_GROUND);
    this.vehicleSpriteId = id;
    this.applyVehicleDepth();
  }

  /** De face, guidon et colonne passent devant le corps ; sinon la trottinette est dessous. */
  private applyVehicleDepth(): void {
    this.vehicleSprite?.setDepth(this._depth + (this.facing === 'down' ? 0.5 : -0.05));
  }

  private destroyVehicleSprite(): void {
    this.vehicleSprite?.destroy();
    this.vehicleSprite = null;
    this.vehicleSpriteId = null;
  }

  /** Empreinte de la planche Pixel Citizen affichée (null = avatar LimeZu en calques). */
  private pcSheet: string | null = null;
  /** Planche Campus (v3) : cadence de marche calée sur la vitesse. */
  private campusSheet = false;
  /** Vitesse lissée (px/s) et position précédente, pour la cadence de marche Campus. */
  private walkSpeed = 0;
  private lastPos: { x: number; y: number } | null = null;
  /**
   * Génération d'apparence : un chargement de planche terminé après un nouveau
   * changement d'apparence ne doit rien afficher (cf. règle « compteur de
   * génération », CLAUDE.md).
   */
  private appearanceGeneration = 0;

  private applyPcSheet(sheet: string, generation: number): void {
    const key = pcTextureKey(sheet);
    const build = () => {
      if (!this.active || generation !== this.appearanceGeneration) return;
      ensurePixelCitizenAnims(this.scene, key);
      // Plus proche voisin pour garder des pixels nets (aussi au zoom de la caméra)
      this.scene.textures.get(key).setFilter(Phaser.Textures.FilterMode.NEAREST);
      const sprite = this.scene.add.sprite(this.x, this.y + PC_FEET * PC_SCALE, key);
      sprite.setOrigin(0.5, 1);
      sprite.setScale(PC_SCALE);
      sprite.setDepth(this._depth);
      this.layerSprites.set('body', sprite);
      this.playCompositeAnimation(this.currentAnim ?? 'idle_down');
    };
    if (this.scene.textures.exists(key)) { build(); return; }

    const onError = (file: Phaser.Loader.File) => {
      if (file.key !== key) return;
      cleanup();
      // Planche introuvable : avatar par défaut plutôt qu'un joueur invisible
      if (!this.active || generation !== this.appearanceGeneration) return;
      console.warn(`[CompositeAvatar] planche introuvable : ${sheet}`);
      loadAppearanceIfNeeded(this.scene, DEFAULT_APPEARANCE, () => {
        if (this.active && generation === this.appearanceGeneration) this.updateAppearance(DEFAULT_APPEARANCE);
      });
    };
    const onComplete = () => { cleanup(); build(); };
    const cleanup = () => {
      this.scene.load.off(Phaser.Loader.Events.FILE_LOAD_ERROR, onError);
      this.scene.load.off(`filecomplete-spritesheet-${key}`, onComplete);
    };
    this.scene.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, onError);
    this.scene.load.once(`filecomplete-spritesheet-${key}`, onComplete);
    this.scene.load.spritesheet(key, pcSheetUrl(sheet), { frameWidth: PC_FRAME, frameHeight: PC_FRAME });
    this.scene.load.start();
  }

  // ── Appearance ──────────────────────────────────────────

  updateAppearance(appearance: AvatarAppearance): void {
    const generation = ++this.appearanceGeneration;
    // Destroy old layer sprites
    for (const sprite of this.layerSprites.values()) {
      sprite.destroy();
    }
    this.layerSprites.clear();

    if (isPcSheetRef(appearance)) {
      this.pcSheet = appearance.sheet;
      this.campusSheet = appearance.v === 3;
      this.applyPcSheet(appearance.sheet, generation);
      return;
    }
    this.pcSheet = null;
    this.campusSheet = false;

    const layerIds: Array<{ slot: string; id: string }> = [
      { slot: 'body',   id: appearance.bodyId },
      { slot: 'eyes',   id: appearance.eyesId },
      { slot: 'outfit', id: appearance.outfitId },
      { slot: 'hair',   id: appearance.hairId },
    ];
    if (appearance.accessoryId) {
      layerIds.push({ slot: 'accessory', id: appearance.accessoryId });
    }

    layerIds.sort(
      (a, b) => LAYER_ORDER.indexOf(a.slot) - LAYER_ORDER.indexOf(b.slot),
    );

    for (let i = 0; i < layerIds.length; i++) {
      const { slot, id } = layerIds[i]!;
      const entry = lookupEntry(id);
      if (!entry) {
        console.warn(`[CompositeAvatar] unknown id: ${id}`);
        continue;
      }

      if (!this.scene.textures.exists(id)) {
        console.warn(`[CompositeAvatar] texture not loaded: ${id}`);
        continue;
      }

      ensureAnimations(this.scene, id);

      const sprite = this.scene.add.sprite(this.x, this.y, id);
      sprite.setOrigin(0.5, 1);
      sprite.setScale(this._scale);
      sprite.setDepth(this._depth + i * 0.1);

      if (entry.recolorable && appearance.hairColor) {
        sprite.setTint(appearance.hairColor);
      }

      this.layerSprites.set(slot, sprite);
    }

    // Resume animation if one was playing
    if (this.currentAnim) {
      this.playCompositeAnimation(this.currentAnim);
    }
  }

  // ── Animations ──────────────────────────────────────────

  playCompositeAnimation(key: string): void {
    this.currentAnim = key;
    this.moving = key.startsWith('walk_');
    // Sur la trottinette : on ne marche pas, on se tient debout.
    const played = this.vehicle ? key.replace(/^walk_/, 'idle_') : key;

    for (const [slot, sprite] of this.layerSprites) {
      const textureKey = sprite.texture.key;
      let fullKey = animKey(textureKey, played);
      // Assis : pose assise (ou frappe) de la direction du siège ; planche sans
      // pose assise (Pixel Citizen, LimeZu) : image de repos.
      if (this.seat) {
        const dir = this.seat.direction;
        const seated = [this.seat.typing ? `type_${dir}` : null, `sit_${dir}`, `idle_${dir}`]
          .filter((k): k is string => k !== null)
          .map((k) => animKey(textureKey, k))
          .find((k) => this.scene.anims.exists(k));
        if (seated) fullKey = seated;
      }

      if (!this.scene.anims.exists(fullKey)) {
        console.warn(`[CompositeAvatar] Animation manquante: ${fullKey} (layer=${slot})`);
        return;
      }

      sprite.play(fullKey, true);
    }
  }

  getCurrentCompositeAnim(): string | null {
    return this.currentAnim;
  }

  // ── UI overlays ─────────────────────────────────────────

  setDisplayName(name: string): void {
    this.fullName = name;
    this.nameLabel.setText(shortDisplayName(name));
  }

  setStatus(status: string): void {
    this.statusDot.setFillStyle(STATUS_HEX[status] ?? STATUS_HEX['available']);
  }

  /** `durationMs` : durée restante du mute (repli : 5 min, la durée posée par l'Admin). */
  setMuted(muted: boolean, durationMs = 300_000): void {
    this._isMuted = muted;
    this.muteBg.setVisible(muted);
    this.muteLabel.setVisible(muted);

    if (this._muteTimer) {
      clearTimeout(this._muteTimer);
      this._muteTimer = null;
    }

    if (muted) {
      this._muteTimer = setTimeout(() => {
        this._isMuted = false;
        this.muteBg.setVisible(false);
        this.muteLabel.setVisible(false);
        this._muteTimer = null;
      }, durationMs);
    }
  }

  // ── Depth / scale override ──────────────────────────────

  override setDepth(depth: number): this {
    this._depth = depth;
    super.setDepth(depth);
    let i = 0;
    for (const sprite of this.layerSprites.values()) {
      sprite.setDepth(depth + i * 0.1);
      i++;
    }
    this.applyVehicleDepth();
    return this;
  }

  setLayerTint(layer: string, hexColor: number): void {
    const sprite = this.layerSprites.get(layer);
    if (!sprite) return;

    const entry = lookupEntry(sprite.texture.key);
    if (entry?.recolorable) {
      sprite.setTint(hexColor);
    }
  }

  /** Cadence de marche Campus : vitesse mesurée sur le déplacement de l'ancre, lissée. */
  private syncWalkCadence(delta: number): void {
    const last = this.lastPos;
    this.lastPos = { x: this.x, y: this.y };
    if (!last || delta <= 0) return;
    const speed = Math.hypot(this.x - last.x, this.y - last.y) / (delta / 1000);
    this.walkSpeed += (speed - this.walkSpeed) * CAMPUS_SPEED_SMOOTHING;
    const body = this.layerSprites.get('body');
    if (body) body.anims.timeScale = this.moving ? Phaser.Math.Clamp(this.walkSpeed / CAMPUS_STEP_SPEED, 0.6, 3) : 1;
  }

  // ── Recalage des calques et de l'étiquette sur l'ancre ──

  /**
   * Appelé juste avant le rendu (PRE_RENDER), après le pas de physique : les
   * images suivent la position de l'ancre de cette image-ci, comme la caméra.
   * Fait dans `preUpdate`, avant la physique, l'avatar était dessiné avec une
   * image de retard sur la caméra, et ce retard variait avec la durée de
   * chaque image : l'avatar tressautait par rapport au décor.
   */
  syncToAnchor(time: number, delta: number): void {
    if (!this.active) return;

    // Sync layer sprites to anchor position (remontés sur le plateau d'une trottinette)
    let lift = 0;
    let shift = 0;
    if (this.vehicle) {
      this.syncVehicleSprite();
      this.vehicleSprite?.setPosition(this.x - this.vehicleSprite.width / 2, this.y + RIDE_GROUND);
      this.applyVehicleDepth();
      lift = RIDE_LIFT[this.facing] + (this.moving && Math.floor(time / RIDE_BOB_MS) % 2 ? 1 : 0);
      shift = RIDE_SHIFT[this.facing];
    } else if (this.seat && this.campusSheet) {
      lift = this.seat.lift;
    }
    for (const sprite of this.layerSprites.values()) {
      sprite.setPosition(this.x + shift, this.y + (this.pcSheet ? PC_FEET * PC_SCALE : 0) - lift);
    }
    if (this.campusSheet) this.syncWalkCadence(delta);

    // Sync UI overlays — pastille : [• Prénom], centrée au-dessus de la tête
    const tw = this.nameLabel.width;
    this.labelWidth = LABEL_PAD_X * 2 + LABEL_DOT_R * 2 + LABEL_DOT_GAP + tw;

    // Empilement anti-chevauchement : calculé une fois par frame pour toute la scène
    const avatars = avatarsByScene.get(this.scene);
    const frame = this.scene.game.loop.frame;
    if (avatars && lastLayoutFrame.get(this.scene) !== frame) {
      lastLayoutFrame.set(this.scene, frame);
      layoutLabels(avatars);
    }
    const nameY = this.labelBottom || this.naturalLabelBottom;
    const left = this.x - this.labelWidth / 2;
    const midY = nameY - LABEL_H / 2;

    if (this.drawnBgWidth !== this.labelWidth) {
      this.drawnBgWidth = this.labelWidth;
      this.nameBg.clear();
      this.nameBg.fillStyle(LABEL_BG, LABEL_BG_ALPHA);
      this.nameBg.fillRoundedRect(-this.labelWidth / 2, -LABEL_H, this.labelWidth, LABEL_H, LABEL_RADIUS);
    }
    this.nameBg.setPosition(this.x, nameY);
    this.nameBg.setDepth(UI_DEPTH);

    this.statusDot.setPosition(left + LABEL_PAD_X, midY);
    this.statusDot.setDepth(UI_DEPTH + 1);

    this.nameLabel.setPosition(left + LABEL_PAD_X + LABEL_DOT_R * 2 + LABEL_DOT_GAP, midY);
    this.nameLabel.setDepth(UI_DEPTH + 1);

    if (this._isMuted) {
      const muteY = nameY - LABEL_H - 4;
      this.muteBg.setPosition(this.x, muteY);
      this.muteBg.setSize(this.muteLabel.width + 10, this.muteLabel.height + 4);
      this.muteBg.setDepth(UI_DEPTH + 2);
      this.muteLabel.setPosition(this.x, muteY);
      this.muteLabel.setDepth(UI_DEPTH + 3);
    }
  }

  // ── Cleanup ─────────────────────────────────────────────

  /**
   * Le point (coordonnées du monde) tombe-t-il sur le personnage affiché ?
   * L'ancre est invisible : Phaser ne lui envoie aucun clic, d'où ce test
   * fait par la scène (coucou au double-clic). Rectangle centré sur la
   * silhouette, plus étroit que la case de la planche (voisins proches).
   */
  containsWorldPoint(x: number, y: number): boolean {
    for (const sprite of this.layerSprites.values()) {
      if (!sprite.visible) continue;
      const w = sprite.displayWidth * 0.5;
      const h = sprite.displayHeight * 0.85;
      if (x >= sprite.x - w / 2 && x <= sprite.x + w / 2 && y >= sprite.y - h && y <= sprite.y) return true;
    }
    return false;
  }

  override destroy(fromScene?: boolean): void {
    if (this._muteTimer) clearTimeout(this._muteTimer);
    if (this.scene) avatarsByScene.get(this.scene)?.delete(this);
    for (const sprite of this.layerSprites.values()) {
      sprite.destroy();
    }
    this.layerSprites.clear();
    this.destroyVehicleSprite();
    this.nameBg.destroy();
    this.nameLabel.destroy();
    this.statusDot.destroy();
    this.muteBg.destroy();
    this.muteLabel.destroy();
    super.destroy(fromScene);
  }
}

// ── Exported default appearance ─────────────────────────────────────────────

export { DEFAULT_APPEARANCE };

/**
 * Preload only the textures needed for a single player's appearance.
 * Call in a scene's preload() for the local player.
 */
export function preloadPlayerAppearance(
  scene: Phaser.Scene,
  appearance: AvatarAppearance,
): void {
  // Planche Pixel Citizen : chargée par CompositeAvatar.updateAppearance()
  if (isPcSheetRef(appearance)) return;
  const ids = [
    appearance.bodyId,
    appearance.eyesId,
    appearance.outfitId,
    appearance.hairId,
    appearance.accessoryId,
  ];

  for (const id of ids) {
    if (!id) continue;
    if (scene.textures.exists(id)) continue;
    const entry = lookupEntry(id);
    if (!entry) continue;
    scene.load.spritesheet(id, entry.url, {
      frameWidth: FRAME_W,
      frameHeight: FRAME_H,
    });
  }
}

/**
 * Load appearance textures on demand (for remote players joining mid-game).
 * Checks cache first; only triggers a load + callback if needed.
 */
export function loadAppearanceIfNeeded(
  scene: Phaser.Scene,
  appearance: AvatarAppearance,
  onComplete?: () => void,
): void {
  // Planche Pixel Citizen : chargée par CompositeAvatar.updateAppearance()
  if (isPcSheetRef(appearance)) {
    onComplete?.();
    return;
  }
  const ids = [
    appearance.bodyId,
    appearance.eyesId,
    appearance.outfitId,
    appearance.hairId,
    appearance.accessoryId,
  ];

  const toLoad: CatalogEntry[] = [];
  for (const id of ids) {
    if (!id) continue;
    if (scene.textures.exists(id)) continue;
    const entry = lookupEntry(id);
    if (entry) toLoad.push(entry);
  }

  if (toLoad.length === 0) {
    onComplete?.();
    return;
  }

  for (const entry of toLoad) {
    scene.load.spritesheet(entry.id, entry.url, {
      frameWidth: FRAME_W,
      frameHeight: FRAME_H,
    });
  }

  if (onComplete) {
    scene.load.once(Phaser.Loader.Events.COMPLETE, onComplete);
  }
  scene.load.start();
}
